/**
 * Group-specific admin handler
 * 
 * Extends base entity admin with Group-specific functionality:
 * - Group member management
 * - Workspace group management
 * - Statistics
 */

import { useState, useCallback, useEffect } from "react";
import type { partyStructureTypes } from "@pineseed/spec";
import { useBaseEntityAdmin, type EntityAdminHandler } from "./useEntityAdmin";
import type { UseAdminAppReturn } from "./useAdminApp";

type ParticipantGroup = partyStructureTypes["ParticipantGroup"];
type Participant = partyStructureTypes["Participant"];

export interface GroupAdminHandler extends EntityAdminHandler<ParticipantGroup> {
  // Group member management
  loadGroupMembers: (groupId: string) => Promise<Participant[]>;
  
  // Statistics
  getStats: () => {
    total: number;
    totalMembers: number;
    averageMembersPerGroup: number;
  };
  
  // Members cache
  groupMembers: Record<string, Participant[]>;
  groupMembersLoading: boolean;
}

export function useGroupAdmin(adminApp: UseAdminAppReturn): GroupAdminHandler {
  // Base CRUD operations
  // Pattern: LIST uses 'groups' (plural), CRUD uses 'group' (singular)
  const baseHandler = useBaseEntityAdmin<ParticipantGroup>({
    adminApp,
    endpoint: 'groups', // For GET /groups (list)
    crudEndpoint: 'group', // For POST/PUT/DELETE /group
    entityName: 'group',
    extractFromResponse: (data: any) => data.groups || data || [],
  });

  // Group members cache
  const [groupMembers, setGroupMembers] = useState<Record<string, Participant[]>>({});
  const [groupMembersLoading, setGroupMembersLoading] = useState(false);

  // Load group members
  const loadGroupMembers = useCallback(async (groupId: string): Promise<Participant[]> => {
    setGroupMembersLoading(true);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/participants/group?groupId=${groupId}`,
        {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );
      if (!response.ok) throw new Error('Failed to load group members');
      const data = await response.json();
      const members = data.participants || data || [];
      
      setGroupMembers(prev => ({ ...prev, [groupId]: members }));
      console.log('Loaded group members:', groupId, members.length);
      
      return members;
    } catch (error) {
      console.error('Failed to load group members:', error);
      return [];
    } finally {
      setGroupMembersLoading(false);
    }
  }, [adminApp]);

  // Get statistics
  const getStats = useCallback(() => {
    const totalMembers = Object.values(groupMembers).reduce(
      (sum, members) => sum + members.length,
      0
    );

    return {
      total: baseHandler.entities.length,
      totalMembers,
      averageMembersPerGroup: baseHandler.entities.length > 0 
        ? Math.round(totalMembers / baseHandler.entities.length) 
        : 0,
    };
  }, [baseHandler.entities, groupMembers]);

  // Load members for all groups when groups change
  useEffect(() => {
    if (baseHandler.entities.length > 0) {
      baseHandler.entities.forEach(group => {
        if (!groupMembers[group.id]) {
          loadGroupMembers(group.id);
        }
      });
    }
  }, [baseHandler.entities.length]); // Only trigger when count changes

  return {
    // Base operations
    ...baseHandler,
    
    // Group-specific operations
    loadGroupMembers,
    getStats,
    
    // Group members state
    groupMembers,
    groupMembersLoading,
  };
}

